import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { CheckCircle, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const QuoteForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    serviceType: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Get the backend URL from environment variable
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Submit to backend
      const response = await fetch(`${backendUrl}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Show success message
        setIsSubmitted(true);
        toast({
          title: "Quote Request Received!",
          description: "We'll contact you within 24 hours. Call 306-262-5995 for immediate service.",
        });

        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            address: '',
            serviceType: '',
            message: ''
          });
          setIsSubmitted(false);
        }, 3000);
      } else {
        throw new Error('Failed to submit quote request');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast({
        title: "Submission Error",
        description: "Please try again or call us at 306-262-5995",
        variant: "destructive"
      });
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-[#0F0F11] border border-[#D4AF37] rounded-3xl shadow-2xl p-12 text-center">
          <div className="flex items-center justify-center w-20 h-20 bg-[#D4AF37]/10 rounded-full mx-auto mb-6">
            <CheckCircle size={48} className="text-[#D4AF37]" />
          </div>
          <h3 className="text-3xl font-bold text-white mb-4">
            Thank You!
          </h3>
          <p className="text-xl text-neutral-300 mb-6">
            Your quote request has been received. We'll contact you within 24 hours.
          </p>
          <p className="text-lg text-neutral-400">
            Need immediate assistance?{' '}
            <a href="tel:306-262-5995" className="text-[#D4AF37] font-semibold hover:text-[#F0C84A]">
              Call us at 306-262-5995
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0F0F11] border border-white/10 rounded-3xl shadow-2xl p-8 md:p-12">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-white">Full Name *</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John Smith"
              className="h-12 bg-[#050505] border-white/10 text-white placeholder:text-neutral-500 focus:border-[#D4AF37]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone" className="text-white">Phone Number *</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="306-XXX-XXXX"
              className="h-12 bg-[#050505] border-white/10 text-white placeholder:text-neutral-500 focus:border-[#D4AF37]"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-white">Email Address *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="john@example.com"
            className="h-12 bg-[#050505] border-white/10 text-white placeholder:text-neutral-500 focus:border-[#D4AF37]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="address" className="text-white">Service Address</Label>
          <Input
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            placeholder="123 Main St, Regina, SK"
            className="h-12 bg-[#050505] border-white/10 text-white placeholder:text-neutral-500 focus:border-[#D4AF37]"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="serviceType" className="text-white">Type of Service Needed *</Label>
          <select
            id="serviceType"
            name="serviceType"
            value={formData.serviceType}
            onChange={handleChange}
            required
            className="w-full h-12 px-3 py-2 bg-[#050505] border border-white/10 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
          >
            <option value="" className="bg-[#050505]">Select a service...</option>
            <option value="installation" className="bg-[#050505]">New Installation</option>
            <option value="repair" className="bg-[#050505]">Repair</option>
            <option value="opener" className="bg-[#050505]">Opener Installation/Repair</option>
            <option value="spring" className="bg-[#050505]">Spring Replacement</option>
            <option value="maintenance" className="bg-[#050505]">Maintenance</option>
            <option value="emergency" className="bg-[#050505]">Emergency Service</option>
            <option value="other" className="bg-[#050505]">Other</option>
          </select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message" className="text-white">Additional Details</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Please describe your garage door issue or project..."
            rows={5}
            className="resize-none bg-[#050505] border-white/10 text-white placeholder:text-neutral-500 focus:border-[#D4AF37]"
          />
        </div>

        <Button 
          type="submit"
          size="lg"
          className="w-full bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050505] font-semibold text-lg py-6 shadow-lg hover:shadow-xl transition-all"
        >
          <Send size={20} className="mr-2" />
          Request Free Quote
        </Button>

        <p className="text-sm text-neutral-500 text-center">
          By submitting this form, you agree to be contacted regarding your quote request.
        </p>
      </form>
    </div>
  );
};

export default QuoteForm;
